import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import client from '../../apollo-client';
import TourBasics from '../../components/TourPage/TourBasics';
import Included from '../../components/TourPage/Included';
import { PaxContext } from '../../utils/context/checkoutContext';
import AddReviewModal from '../../components/TourPage/ProductReviews/AddReviewModal';
const Link = dynamic(() => import('next/link'));
import Head from 'next/head';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { view_item } from '../../utils/googleTransaction';
const RelatedTours = dynamic(
  () => import('../../components/TourPage/RelatedTours')
);
const Newsletter = dynamic(() => import('../../layouts/Newsletter/Newsletter'));
import { useUpdateQueryParams } from '../../utils/useUpdateQueryParams';
import { useSearchContext } from '../../utils/context/searchContext';
import { GetServerSideProps } from 'next';
import ShareButton from '../../shared-components/AttractionCard/ShareButton';
import Footer from '../../layouts/Footer/footer';
import { WhatYouWillSeeSection } from '../../components/TourPage/WhatYouWillSeeSection';
import { NeedToKnow } from '../../components/TourPage/NeedToKnow';
import { BOOKING_DATA } from '../../api/fetch_user_details';
const ReviewsSection = dynamic(
  () => import('../../components/TourPage/ProductReviews/ProductReviews')
);
import { Review } from '../[slug]/[slug2]';
import axios from 'axios';
import { useAuthContext } from '../../utils/context/authContext';
import { BestReview, Product } from '../../types/pages/tour';
import dynamic from 'next/dynamic';
import { replaceTripshepherdWithSeeSight } from '../../utils/trip-sheppered-text';
import scrollToElement from '../../utils/scrollIntoView';
import { useMediaQuery } from '../../hooks/mediaQuery';
import UPDATE_BOOKING_REVIEW_STATUS, {
  FETCH_BOOKING_REVIEW
} from '../../api/updateBookingReviewStatus';
import { useTransformReview } from '../../utils/transformReview';
import { toast } from 'react-toastify';
import { useLazyQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
const PhotosHeader = dynamic(
  () => import('../../components/TourPage/PhotosHeader')
);
const DynamicMap = dynamic(
  () => import('../../components/TourPage/ProductAttractionsMap'),
  { ssr: false }
);
interface ButtonArray {
  id: string;
  label: string;
}

interface TourPageProps {
  product: Product;
  initialReviews: Review[];
  modifiedBestReview: BestReview;
  modifiedSecondMostPositiveReview: Review;
  modifiedPositiveReview: Review;
  totalCount: number;
  count: number;
}

interface BookingData {
  boatnew_booking: Array<{
    tour_date: string;
    product: {
      id: string;
      strapi: {
        id: string;
      };
      cities_products: Array<{
        city: {
          name: string;
          id: string;
          strapi_city: {
            id: string;
          };
        };
      }>;
    };
    customer: {
      contact_name: string;
      email: string;
    };
  }>;
}
const TourPage = ({
  product,
  initialReviews,
  modifiedBestReview,
  modifiedSecondMostPositiveReview,
  modifiedPositiveReview,
  totalCount,
  count
}: TourPageProps) => {
  const router = useRouter();

  //@ts-ignore
  const { updateTour, currency, updateCurrency } = useContext(PaxContext);

  // State to store the selected price based on the current currency
  const [selectedPrice, setSelectedPrice] = useState<
    Price | null | undefined
  >();

  const [showReviewModal, setShowReviewModal] = useState(false);

  const [reviewFormData, setReviewFormData] = useState({
    name: '',
    email: '',
    rating: 0,
    reviewTitle: '',
    reviewText: '',
    tourDate: '',
    productId: '',
    cityId: ''
  });
  const [bookingId, setBookingId] = useState('');

  const [isValidBooking, setIsValidBooking] = useState(false);

  const [bookingCheckComplete, setBookingCheckComplete] = useState(false);

  const [bookingErrorMessage, setBookingErrorMessage] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [selectedReviewData, setSelectedReviewData] = useState<Review | null>(
    null
  );

  useEffect(() => {
    const { id } = router.query;
    if (id && typeof id === 'string') {
      setBookingId(id);
      checkBookingValidity(id);
    } else {
      setBookingCheckComplete(true);
    }
  }, [router.query]);

  const checkBookingValidity = async (bookingId: string) => {
    try {
      const response = await client.query({
        query: BOOKING_DATA,
        variables: { bookingId }
      });

      const bookingData: BookingData = response.data;

      if (bookingData.boatnew_booking.length === 0) {
        setIsValidBooking(false);
        setBookingErrorMessage(`Booking ID ${bookingId} does not exist.`);
      } else {
        const booking = bookingData.boatnew_booking[0];

        if (booking.product.id === product.id) {
          setIsValidBooking(true);
          setReviewFormData({
            name: booking.customer.contact_name || '',
            email: booking.customer.email || '',
            rating: 0,
            reviewTitle: '',
            reviewText: '',
            tourDate: booking.tour_date || '',
            productId: product.id,
            cityId:
              booking.product.cities_products[0]?.city.strapi_city.id || ''
          });
          setShowReviewModal(true);
          setTimeout(() => {
            const reviewsSection = document.getElementById('important-info');
            if (reviewsSection) {
              reviewsSection.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
          }, 100);
        } else {
          setIsValidBooking(false);
          setBookingErrorMessage(
            `Booking ID ${bookingId} is not valid for this product.`
          );
        }
      }
    } catch (error) {
      console.error('Error checking booking validity:', error);
      setIsValidBooking(false);
      setBookingErrorMessage('An error occurred while checking the booking.');
    } finally {
      setBookingCheckComplete(true);
    }
  };

  useEffect(() => {
    if (product.private) updateCurrency('USD');
    else updateCurrency(currency);

    const price = Array.isArray(product?.productPrices)
      ? product.productPrices.find(price => price.currency === currency)
      : null;
    setSelectedPrice(price);
  }, [currency, product?.productPrices]);

  const buttonArray: ButtonArray[] = [
    { id: 'need-to-know', label: 'Need to know' },
    { id: 'tour-highlight', label: 'Tour Highlight' },
    { id: 'map', label: 'Map' }
  ];

  const descriptionRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (router.asPath.includes('#reviews')) {
      const cleanPath = router.asPath.split('#')[0];
      router.replace(cleanPath, undefined, { shallow: true });
      window.scrollTo(0, 0);
    }
  }, [router]);

  useEffect(() => {
    view_item({
      item_id: product?.id,
      item_name: product?.name,
      item_price: JSON.stringify(selectedPrice?.prices.adult),
      currency_code: currency
    });
  }, []);
  const scrollToSection = (elementId: string, offset: number = 0) => {
    const element = document.getElementById(elementId);
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset + offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  //@ts-ignore
  const reviewsAggregate = product?.pageContent?.reviews_aggregate;
  const reviewCount: number = reviewsAggregate?.aggregate?.count || 0;
  const totalRating: number = reviewsAggregate?.aggregate?.sum?.rating || 0;
  const averageRating: number =
    reviewCount > 0 ? parseFloat((totalRating / reviewCount).toFixed(1)) : 0;
  const [bestReview, setBestReview] = useState<BestReview>();
  const [secondMostPositiveReview, setSecondMostPositiveReview] =
    useState<Review>();
  const [positiveReview, setPositiveReview] = useState<Review>();

  const [reviews, setReviews] = useState<Review[]>(initialReviews);
  const [selectedRating, setSelectedRating] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [reviewsCount, setReviewsCount] = useState(count);
  const [totalReviewsCount, setTotalReviewsCount] = useState(totalCount);

  const [loader, setLoader] = React.useState(false);
  const { user }: any = useAuthContext();
  const [showPax, setShowPax] = useState<boolean>(false);
  const [isPaxVisible, setIsPaxVisible] = useState<boolean>(false);
  const paxRef = useRef<HTMLDivElement>(null);
  const itemsPerPage = 5;

  useEffect(() => {
    // Reset states when the route changes
    setShowPax(false);
    setIsPaxVisible(false);
  }, [router.query.slug]);

  const fetchReviews = async () => {
    try {
      setLoader(true);
      const response = await axios.get(
        `${
          process.env.NEXT_PUBLIC_FIREBASE_WEBSITE_CONNECTION
        }/product-review/${product.id}?sortBy=created_at&rating=${
          selectedRating === 'all-stars' ? '' : selectedRating
        }&sortOrder=desc&page=${currentPage}&limit=${itemsPerPage}`
      );

      // Process main reviews
      if (response?.data?.reviews) {
        setReviews(response?.data?.reviews);
        setBestReview(response?.data?.bestReview);
        setSecondMostPositiveReview(response?.data?.secondMostPositiveReview);
        setPositiveReview(response?.data?.positiveReview);
        setReviewsCount(response.data.reviewsCount || 0);
        setTotalReviewsCount(response.data.totalReviewsCount || 0);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
    setLoader(false);
  };

  useEffect(() => {
    fetchReviews();
  }, [selectedRating, currentPage]);

  const updateTourContext = () => {
    updateTour({
      tour: product?.name,
      cardMessage: product?.cardMessage,
      //@ts-ignore
      tourId: product?.id,
      tourImage: product?.pageContent?.carousel?.map(
        (item: any) => item.url
      )[0],
      private: product?.private,
      ratingCount: reviewCount,
      ratingAvg: averageRating,
      guest: reviewCount,
      message: product?.cardMedia,
      city: product?.pageContent?.city,
      rezdyId: product?.duplicateRezdyId
        ? product?.duplicateRezdyId
        : product?.RezdyId
    });
  };
  const productSchema = {
    '@context': 'http://schema.org',
    '@type': 'Product',
    name: product?.name,
    image: product?.pageContent?.carousel[0]?.url,
    description: product?.pageContent?.meta_description,

    offers: {
      '@type': 'Offer',
      priceCurrency: currency,
      price: selectedPrice?.prices?.adult || product?.price,
      availability: 'InStock',
      priceValidUntil: '2023-12-31',
      seller: {
        '@type': 'Organization',
        name: 'See Sight Tours'
      }
    },
    hasMerchantReturnPolicy: {
      '@type': 'MerchantReturnPolicy',
      returnFees: 'Free Cancellation 24 hours in advance',
      returnPolicyCountry: 'CA'
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: averageRating || 4.9,
      bestRating: 5,
      ratingCount: reviewCount && reviewCount > 0 ? Math.floor(reviewCount) : 10
    },
    review: {
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: bestReview?.rating || 5,
        bestRating: 5
      },
      author: {
        '@type': 'Person',
        name: bestReview?.name || 'John Doe'
      },
      datePublished: bestReview?.created_at
        ? dayjs(bestReview.created_at).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD')
    }
  };
  const tourSchema = {
    '@context': 'https://schema.org',
    '@type': 'TouristTrip',
    name: product?.name,
    description: product?.pageContent?.short_description,
    itinerary: product?.attractions?.map((item: any) => {
      return {
        '@type': 'TouristAttraction',
        name: item?.name,
        description: item?.cardSnippet,
        image: item?.heroMedia?.length > 0 ? item?.heroMedia[0].url : ''
      };
    }),
    touristType: 'Sightseeing',

    offers: {
      '@type': 'Offer',
      url: product?.pageContent?.canonical,
      price: selectedPrice?.prices?.adult || product?.price,
      priceCurrency: currency,
      availability: 'https://schema.org/InStock',
      priceValidUntil: '2023-12-31',
      validFrom: '2023-07-01',
      validThrough: '2023-12-31',
      seller: {
        '@type': 'Organization',
        name: 'See Sight Tours'
      }
    }
  };
  const trackButtonCall = async () => {
    try {
      if (typeof window !== 'undefined' && window.gtag) {
        await window.gtag('event', 'button_click', {
          category: 'interaction',
          action: 'button click',
          label: 'mobile-check-dates'
        });
      }
    } catch (error) {
      console.error('Error tracking button click:', error);
    }
  };
  const MOBILE_THRESHOLD = 768;

  const isMobile = () =>
    typeof window !== 'undefined' && window.innerWidth >= MOBILE_THRESHOLD;

  const [dropdownStates, setDropdownStates] = useState<boolean[]>(
    Array(6).fill(isMobile())
  );
  const [openMenuIndex, setOpenMenuIndex] = useState<string | null>(null);

  const handleOpenMenu = (index: string) => {
    if (openMenuIndex !== index) {
      setOpenMenuIndex(index);
    }
  };

  const handleCloseMenu = () => {
    setOpenMenuIndex(null);
  };
  useEffect(() => {
    if (typeof window !== 'undefined') {
      isMobile && setDropdownStates(Array(6).fill(isMobile()));
    }
  }, [typeof window !== 'undefined' ? window.innerWidth : null]);

  useEffect(() => {
    const handleRouteChange = () => {
      const delay = 2000;
      setTimeout(() => {
        setDropdownStates(Array(dropdownStates?.length).fill(false));
      }, delay);
    };
    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events, dropdownStates]);
  // const extractMaxPeople = (tourIncludes: any) => {
  //   let maxPeople = 7; // Default value
  //   const regex = /(\d+)\s*(people|guests)/i;

  //   tourIncludes.forEach((item: any) => {
  //     const value = item.value ? item.value : item;
  //     const match = value.match(regex);
  //     if (match) {
  //       maxPeople = parseInt(match[1]);
  //     }
  //   });
  //   return maxPeople;
  // };
  // const maxPeople = useMemo(
  //   () => extractMaxPeople(product?.pageContent?.tour_includes || []),
  //   [product]
  // );

  const desc = product?.pageContent?.long_description?.trim();
  let timeline;
  if (desc?.includes('\n\n')) {
    timeline = desc.split('\n\n');
  } else if (desc?.includes('\n')) {
    timeline = desc?.split('\n');
  } else if (desc?.includes('<br><br>')) {
    timeline = desc.split('<br><br>');
  } else {
    timeline = [desc];
  }
  useEffect(() => {
    if (showPax) {
      scrollToElement('mobile-pax');
    }
  }, [showPax]);

  const reviewState = {
    average: Number(averageRating),
    total: reviewCount
  };

  // Add useEffect to handle scroll visibility
  useEffect(() => {
    if (!showPax) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsPaxVisible(entry.isIntersecting);
      },
      {
        threshold: 0.1
      }
    );

    if (paxRef.current) {
      observer.observe(paxRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [showPax]);
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <Head>
        <meta name="og:title" content={`${product?.pageContent?.title} `} />
        <meta
          name="og:description"
          content={product?.pageContent?.meta_description}
        />
        <title>{`${product?.pageContent?.title}`}</title>
        <meta
          name="description"
          content={product?.pageContent?.meta_description}
        />
        <link
          rel="canonical"
          href={replaceTripshepherdWithSeeSight(
            product?.pageContent?.canonical
          )}
        />
        <meta
          name="og:image"
          content={product?.pageContent?.carousel[0]?.url}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(productSchema)
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(tourSchema)
          }}
        />
      </Head>
      {router.isFallback ? (
        <div role="status" className="w-full flex justify-center">
          <svg
            aria-hidden="true"
            className="w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <span className="sr-only">Loading...</span>
        </div>
      ) : (
        <>
          <div className="flex flex-col w-full px-[5%] msm:px-[6%] lg:px-[8%] xl:px-[12%] 2xl:px-[14%] py-[3%] bg-[#]">
            <div className="md:hidden flex">
              <Link
                className="font-semibold text-[13px] leading-[19.5px] flex gap-4 p-4 cursor-pointer hover:underline md:text-[16px] md:leading-[24px] md:font-[600]"
                href="#"
                onClick={() => router.push('/')}
              >
                <Image
                  src={'/black-left-arrow.svg'}
                  alt="left arrow"
                  width={8}
                  height={10}
                />
                Back to Tour Listing
              </Link>
            </div>
            <PhotosHeader
              media={product?.pageContent?.carousel}
              productName={product?.name}
              productSlug={product?.slug}
              cardMessage={product?.cardMessage}
            />
            <div ref={descriptionRef}>
              <div className="mt-[35px] md:mt-[65px] w-full gap-[10%] flex flex-row">
                <div className=" w-full mmd:w-[60%]">
                  <div className="flex flex-col justify-start mb-4 gap-2">
                    <div className="flex justify-start w-full gap-[35px] ">
                      <h1 className=" text-lg mt-2 lg:text-2xl font-semibold text-start ">
                        <span className=" font-black "> </span> {product?.name}{' '}
                      </h1>
                      <div className="flex justify-center mt-2">
                        {' '}
                        <ShareButton
                          isOpen={openMenuIndex === product.id}
                          onOpen={() => handleOpenMenu(product.id)}
                          onClose={handleCloseMenu}
                          slug={product.slug}
                        />
                      </div>
                    </div>

                    {reviewCount > 0 && (
                      <div className="flex gap-2 cursor-pointer ">
                        <span className="flex items-center gap-3">
                          <div className="flex items-center">
                            <Image
                              src={'/rating-star-yellow.svg'}
                              width={17}
                              height={17}
                              alt="rating star"
                            />
                            <span>{averageRating}</span>

                            <span className="text-gray-600">/</span>
                            <span>5</span>
                          </div>
                          <span
                            onClick={() => {
                              scrollToSection('reviews-section', -100);
                            }}
                            className="text-black ml-1 underline hover:text-primary hover:font-semibold"
                          >{`${reviewCount} reviews`}</span>
                        </span>
                      </div>
                    )}

                    <div className="flex mt-4 lg:mt-0  justify-start flex-col w-full border-b-2 border-[#EEEE] ">
                      <div className="mmd:block py-[10px]">
                        <TourBasics
                          duration={product?.duration}
                          name={product?.name}
                        />
                      </div>

                      <div className="py-4">
                        <p className="text-sm md:text-base text-black13">
                          {product?.pageContent?.short_description}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div
                    ref={paxRef}
                    id="mobile-pax"
                    className="flex mmd:hidden  justify-center"
                  >
                    <Included
                      shortDescription={product?.pageContent?.short_description}
                      longDescription={product?.pageContent?.long_description}
                      data={product?.pageContent?.tour_includes}
                      attractions={product?.attractions}
                      rezdyId={
                        product?.duplicateRezdyId
                          ? product?.duplicateRezdyId
                          : product?.RezdyId
                      }
                      reviewState={reviewState}
                      bestReview={bestReview}
                      updateTourContext={updateTourContext}
                      duration={product?.duration}
                      privateTour={product?.private}
                      showPax={showPax}
                      //@ts-ignore
                      tourId={
                        product?.duplicateRezdyId
                          ? product?.duplicateRezdyId
                          : product?.RezdyId
                          ? product?.RezdyId
                          : product?.id
                      }
                      adult={selectedPrice?.prices.adult || null}
                      infant={selectedPrice?.prices.infant || null}
                      child={selectedPrice?.prices.child || null}
                      name={product?.name}
                      slug={product?.slug}
                      city={product?.pageContent?.city}
                      bookingCutOffTime={product?.booking_cutoff}
                    />
                  </div>
                  <div className="my-4">
                    {product?.pageContent?.tour_includes?.length > 0 && (
                      <NeedToKnow
                        info={product?.pageContent?.tour_includes}
                        heading="What's Included"
                        showTimeline={false}
                      />
                    )}
                  </div>

                  <div className="my-4">
                    {product?.attractions && (
                      <WhatYouWillSeeSection
                        product={product}
                        showModal={showModal}
                        setShowModal={setShowModal}
                      />
                    )}
                  </div>

                  <div className="my-4">
                    {timeline && (
                      <NeedToKnow
                        info={timeline}
                        heading="What you'll do"
                        showTimeline={true}
                      />
                    )}
                  </div>

                  <div className="my-4">
                    <h2 className="text-lg lg:text-xl font-semibold mb-4">
                      Tour Itineraries
                    </h2>
                    <DynamicMap
                      attractions={product?.attractions}
                      coordinates={product?.pageContent?.city?.coordinates}
                    />
                  </div>

                  <div className="border-b-2 my-8 border-[#EEEE]"></div>
                  {product?.pageContent?.important_info?.length > 0 && (
                    <div>
                      <h2
                        className="text-lg mt-2 lg:text-xl font-semibold text-start"
                        id="important-info"
                      >
                        Important Information
                      </h2>
                      <div className="text-sm md:text-base my-4 leading-6 md:leading-8 text-black13">
                        {product?.pageContent?.important_info?.map(
                          (item: any, index: number) => (
                            <p key={index + 1} className="">
                              {`${
                                typeof item === 'object' ? item.value : item
                              }`}
                            </p>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="w-full mmd:w-[30%]">
                  <div className="relative hidden mmd:flex mmd:sticky mmd:top-[100px] z-[39] h-auto justify-end w-full items-end">
                    <Included
                      shortDescription={product?.pageContent?.short_description}
                      longDescription={product?.pageContent?.long_description}
                      data={product?.pageContent?.tour_includes}
                      attractions={product?.attractions}
                      rezdyId={
                        product?.duplicateRezdyId
                          ? product?.duplicateRezdyId
                          : product?.RezdyId
                      }
                      reviewState={reviewState}
                      bestReview={bestReview}
                      updateTourContext={updateTourContext}
                      duration={product?.duration}
                      privateTour={product?.private}
                      showPax={showPax}
                      //@ts-ignore
                      tourId={
                        product?.duplicateRezdyId
                          ? product?.duplicateRezdyId
                          : product?.RezdyId
                          ? product?.RezdyId
                          : product?.id
                      }
                      adult={selectedPrice?.prices.adult || null}
                      infant={selectedPrice?.prices.infant || null}
                      child={selectedPrice?.prices.child || null}
                      name={product?.name}
                      slug={product?.slug}
                      city={product?.pageContent?.city}
                      bookingCutOffTime={product?.booking_cutoff}
                    />
                  </div>
                </div>
              </div>
              <div id="reviews-section">
                <ReviewsSection
                  rating={Number(averageRating)}
                  totalCount={reviewCount}
                  reviews={reviews}
                  fetchReviews={fetchReviews}
                  setReviews={setReviews}
                  id={product?.id}
                  productName={product?.name}
                  positiveReview={positiveReview}
                  secondMostPositiveReview={secondMostPositiveReview}
                  currentPage={currentPage}
                  setSelectedRating={setSelectedRating}
                  selectedRating={selectedRating}
                  totalReviewsCount={totalReviewsCount}
                  reviewsCount={reviewsCount}
                  setCurrentPage={setCurrentPage}
                />
              </div>
              <div>
                <RelatedTours relatedProducts={product?.relatedProducts} />
              </div>
            </div>
          </div>

          <div ref={footerRef}>
            <Newsletter />
            <Footer />
          </div>

          {(!showPax || (showPax && !isPaxVisible)) && !showModal && (
            <div className="z-[1099] fixed mmd:hidden px-[4%] bg-white items-center border-gray-400 flex justify-between bottom-0 right-0 w-full py-4 rounded-md text-black">
              <div className="font-bold">
                <span className="text-sm font-semibold">From</span> $
                <span>
                  {selectedPrice?.prices?.adult
                    ? Number(selectedPrice?.prices?.adult)
                    : Number(product?.price)}
                  &nbsp;
                  {currency}
                </span>
              </div>

              <button
                className="bg-primary text-white min-w-[30%] px-4 py-2 rounded-lg"
                id="mobile-check-dates"
                onClick={() => {
                  trackButtonCall();
                  if (!showPax) {
                    setShowPax(true);
                    setTimeout(() => {
                      scrollToSection('mobile-pax', -60);
                    }, 100);
                  } else {
                    scrollToSection('mobile-pax', -60);
                  }
                }}
              >
                Check Dates
              </button>
            </div>
          )}
        </>
      )}
      {showReviewModal && (
        <AddReviewModal
          onClose={() => setShowReviewModal(false)}
          initialData={reviewFormData}
          onSubmit={async (reviewData: any) => {
            try {
              const headers = {
                'Content-Type': 'application/json'
              };

              const payload = {
                bookingId: bookingId,
                productId: product.id,
                rating: reviewData.rating,
                review: reviewData.review,
                userName: reviewData.name || reviewFormData?.name
              };

              const response = await axios.post(
                `${process.env.NEXT_PUBLIC_FIREBASE_WEBSITE_CONNECTION}/strapi-product-review`,
                payload,
                { headers, timeout: 10000 }
              );

              if (
                response.data &&
                response.data.message === 'Review added successfully'
              ) {
                fetchReviews();
                setShowReviewModal(false);
                setBookingId('');
                toast.success('Your review has been submitted successfully!');
              }
            } catch (error) {
              console.error('Full error details:', error);
              if (axios.isAxiosError(error)) {
                if (error.response?.status === 401) {
                  console.log(
                    'Authentication failed. Please refresh and try again.'
                  );
                } else {
                  toast.error(
                    error.response?.data?.error || 'Failed to submit review'
                  );
                }
              } else {
                console.log('Network error. Please check your connection.');
              }
            }
          }}
          id={product.id}
          fetchReviews={fetchReviews}
          setIsModalOpen={setIsModalOpen}
          selectedReviewData={selectedReviewData}
          setSelectedReviewData={setSelectedReviewData}
        />
      )}
    </>
  );
};

interface Price {
  currency: string;
  prices: {
    adult: number;
    child: number;
    infant: number;
  };
}
export interface RATINGCOUNT {
  1: number;
  2: number;
  3: number;
  4: number;
  5: number;
}
export const getServerSideProps: GetServerSideProps = async ({ params }) => {
  const { slug } = params as { slug: string };
  try {
    // Fetch product data
    const productRes = await fetch(
      `${process.env.NEXT_PUBLIC_FIREBASE_WEBSITE_CONNECTION}/products/${slug}?lang=en`
    );

    if (!productRes.ok) {
      console.error(`Failed to fetch product data for slug: ${slug}`);
      return { notFound: true };
    }

    const productData = await productRes.json();

    if (!productData.success || !productData.data) {
      console.error(`No product found for slug: ${slug}`);
      return {
        redirect: { destination: '/404', permanent: false }
      };
    }

    // Fetch reviews for the product
    const reviewsRes = await fetch(
      `${process.env.NEXT_PUBLIC_FIREBASE_WEBSITE_CONNECTION}/product-review/${productData.data.id}?sortBy=created_at&rating=5&sortOrder=desc&page=1&limit=5`
    );

    if (!reviewsRes.ok) {
      console.error(`Failed to fetch reviews for slug: ${slug}`);
    }
    const reviewsData = await reviewsRes.json();

    return {
      props: {
        product: replaceTripshepherdWithSeeSight(productData.data),
        initialReviews: reviewsData?.reviews || [],
        modifiedBestReview: reviewsData?.bestReview,
        modifiedSecondMostPositiveReview: reviewsData.secondMostPositiveReview,
        modifiedPositiveReview: reviewsData.positiveReview,
        count: reviewsData?.reviewsCount || 0,
        totalCount: reviewsData?.totalReviewsCount || 0
      }
    };
  } catch (error) {
    console.error('Error fetching product/review data:', error);
    return {
      redirect: { destination: '/404', permanent: false }
    };
  }
};

export default TourPage;
